import { useState, useRef, useEffect, KeyboardEvent } from 'react';
import { Send, Square, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface AIChatInputProps {
  onSend: (message: string) => void;
  onCancel?: () => void;
  isLoading?: boolean;
  isStreaming?: boolean;
  placeholder?: string;
}

export function AIChatInput({
  onSend,
  onCancel,
  isLoading,
  isStreaming,
  placeholder = 'Ask about this profile...',
}: AIChatInputProps) {
  const [message, setMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  }, [message]);

  const handleSubmit = () => {
    const trimmed = message.trim();
    if (!trimmed || isLoading || isStreaming) return;

    onSend(trimmed);
    setMessage('');

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Submit on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleCancel = () => {
    onCancel?.();
  };

  const isDisabled = isLoading || isStreaming;
  const canSend = message.trim().length > 0 && !isDisabled;

  return (
    <div className="flex-shrink-0 border-t border-brand-border bg-brand-surface p-3">
      {/* Quick suggestions */}
      <div className="flex gap-2 mb-2 overflow-x-auto pb-1 scrollbar-hide">
        <QuickSuggestion
          text="Conversation starters"
          icon={<Sparkles className="w-3 h-3" />}
          onClick={() => setMessage('Give me 3 conversation starters based on their profile')}
          disabled={isDisabled}
        />
        <QuickSuggestion
          text="Compatibility"
          onClick={() => setMessage("What are our potential compatibility strengths?")}
          disabled={isDisabled}
        />
        <QuickSuggestion
          text="Red flags?"
          onClick={() => setMessage('Are there any potential concerns I should be aware of?')}
          disabled={isDisabled}
        />
      </div>

      {/* Input area */}
      <div className="flex items-end gap-2">
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={isDisabled}
            rows={1}
            className={cn(
              'w-full resize-none rounded-xl border border-brand-border bg-brand-background px-4 py-2.5 text-sm text-white placeholder:text-brand-muted focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all custom-scrollbar',
              isDisabled && 'opacity-50 cursor-not-allowed'
            )}
          />
        </div>

        {/* Send/Cancel button */}
        {isStreaming ? (
          <Button
            onClick={handleCancel}
            size="icon"
            variant="ghost"
            className="h-10 w-10 rounded-xl bg-red-500/20 text-red-400 hover:bg-red-500/30 hover:text-red-300"
          >
            <Square className="w-4 h-4" />
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={!canSend}
            size="icon"
            className={cn(
              'h-10 w-10 rounded-xl transition-all',
              canSend
                ? 'bg-brand-primary hover:bg-brand-primary/90 text-white'
                : 'bg-brand-surface text-brand-muted'
            )}
          >
            <Send className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Character count */}
      {message.length > 100 && (
        <div className="mt-1 text-right">
          <span
            className={cn(
              'text-xs',
              message.length > 1800 ? 'text-red-400' : 'text-brand-muted'
            )}
          >
            {message.length}/2000
          </span>
        </div>
      )}
    </div>
  );
}

function QuickSuggestion({
  text,
  icon,
  onClick,
  disabled,
}: {
  text: string;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex items-center gap-1.5 px-3 py-1.5 text-xs whitespace-nowrap rounded-full border border-brand-border bg-brand-background text-brand-muted transition-colors',
        disabled
          ? 'opacity-50 cursor-not-allowed'
          : 'hover:border-brand-primary hover:text-white'
      )}
    >
      {icon}
      {text}
    </button>
  );
}
